import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';

import { UserAvatar } from '@bonusly/common-user-popover';
import {
	Box,
	DotsHorizontalOutline,
	Flex,
	HStack,
	Input,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	RefreshOutline,
	rem,
	SearchOutline,
	StopOutline,
	Text,
	Tooltip,
	TrashOutline,
	ZoomInOutline,
} from '@bonusly/design-system';

import { ReactComponent as RepeatHeavyIcon } from '../../../modules/growth/components/one-on-one-meeting/assets/repeat-heavy.svg';
import { ReactComponent as TopicIcon } from '../../../modules/growth/components/one-on-one-meeting/assets/topic.svg';

// ============================================================================
// Utilities
// ============================================================================

function escapeHtml(text: string): string {
	const div = document.createElement('div');
	div.textContent = text;
	// innerHTML escapes <, >, and & but not quotes
	// When used in attributes, we also need to escape double quotes
	return div.innerHTML.replace(/"/g, '&quot;');
}

function generateNodeId(): string {
	return `topic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate the "blank" template for a topic node.
 * For topics: keeps ID and title, but clears content.
 * This is used by the backend to copy repeating topics to the next meeting.
 * @param id - The current node's ID
 * @param title - The topic title
 * @param originalNodeId - The original node ID (for lineage tracking). If not provided, uses the current ID.
 */
export function generateTopicTemplate(id: string, title: string, originalNodeId?: string): string {
	// Use data-node-id for consistency with Repeatable extension
	// Inner paragraph is empty - no data-node-id needed (CreatedByUserExtension will add when edited)
	const origId = originalNodeId || id;
	const template = `<div data-type="topic" data-node-id="${escapeHtml(id)}" data-original-node-id="${escapeHtml(origId)}" data-title="${escapeHtml(title)}" data-repeats="true"><p></p></div>`;
	// Use encodeURIComponent + unescape to handle Unicode characters (emojis, international text)
	// before base64 encoding, matching the pattern in generateBlockTemplate
	try {
		return btoa(unescape(encodeURIComponent(template)));
	} catch {
		// Fallback for any encoding issues
		return btoa(template);
	}
}

// ============================================================================
// Types
// ============================================================================

export interface TopicNodeOptions {
	userId?: string;
}

interface TopicAttributes {
	id: string;
	title: string;
	createdBy?: string;
	repeats?: boolean;
	repeatTemplate?: string;
	copiedFrom?: string;
	copiedByUser?: string;
	originalNodeId?: string;
}

// ============================================================================
// Node View Component
// ============================================================================

const TopicNodeView = ({ node, updateAttributes, deleteNode, extension, editor: _editor, getPos: _getPos }: any) => {
	const { id, title, repeats, createdBy } = node.attrs;
	const [isHovered, setIsHovered] = useState(false);
	const [isMenuHovered, setIsMenuHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const userId = extension.options.userId;
	const titleInputRef = React.useRef<HTMLInputElement>(null);
	const contentWrapperRef = React.useRef<HTMLDivElement>(null);

	// Show menu icon on hover or when menu is open, otherwise show avatar
	const showMenuIcon = isHovered || isMenuHovered || isMenuOpen;

	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newTitle = e.target.value;
		const newAttrs: Record<string, any> = { title: newTitle };

		// Always regenerate template when title changes (if repeating)
		if (repeats) {
			newAttrs.repeatTemplate = generateTopicTemplate(id, newTitle, node.attrs.originalNodeId);
		}

		updateAttributes(newAttrs);
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Handle Enter - move to content
		if (e.key === 'Enter' || e.key === 'ArrowDown') {
			e.preventDefault();
			// Focus the content area below
			requestAnimationFrame(() => {
				const topicContent = contentWrapperRef.current?.querySelector('[data-node-view-content]');
				const editableElement = topicContent?.querySelector('p') as HTMLElement;

				if (editableElement) {
					const range = document.createRange();
					const selection = window.getSelection();
					range.setStart(editableElement, 0);
					range.collapse(true);
					selection?.removeAllRanges();
					selection?.addRange(range);
					editableElement.focus();
				}
			});
			return;
		}

		// Handle Backspace on empty title
		if (e.key === 'Backspace' && !title) {
			e.preventDefault();
			deleteNode();
		}
	};

	const handleToggleRepeat = () => {
		const newRepeats = !repeats;
		// When enabling repeat, set originalNodeId to this node's ID if not already set (first time)
		// When disabling, clear it
		const currentOriginalNodeId = node.attrs.originalNodeId;
		const newOriginalNodeId = newRepeats ? currentOriginalNodeId || id : null;
		const newTemplate = newRepeats ? generateTopicTemplate(id, title, newOriginalNodeId) : null;
		const newCopiedByUser = newRepeats ? userId || null : null;

		updateAttributes({
			repeats: newRepeats,
			repeatTemplate: newTemplate,
			copiedByUser: newCopiedByUser,
			originalNodeId: newOriginalNodeId,
		});
	};

	const handleOpenDetails = () => {
		if (!title) return;
		window.dispatchEvent(
			new CustomEvent('context-topic-selected', {
				detail: {
					topicId: id,
					title: title,
					isGoal: false,
				},
			})
		);
	};

	const handleSearchHistory = () => {
		if (!title) return;
		window.dispatchEvent(new CustomEvent('context-search', { detail: { term: title } }));
	};

	const handleDelete = () => {
		deleteNode();
	};

	return (
		<NodeViewWrapper>
			<Flex
				gap={2}
				mb={4}
				position="relative"
				alignItems="flex-start"
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				maxW="95%"
			>
				{/* Left side: Avatar/Menu Button Column */}
				<Flex position="relative" width={8} height={8} flexShrink={0} alignItems="center" justifyContent="center">
					{/* Menu Button */}
					<Menu onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
						<MenuButton
							data-component="topic-node-menu-button"
							as={Box}
							position="absolute"
							width={8}
							height={8}
							display="flex"
							alignItems="center"
							justifyContent="center"
							borderRadius="full"
							cursor="pointer"
							transition="all 0.3s ease"
							transitionDelay="0.1s"
							onMouseEnter={() => setIsMenuHovered(true)}
							onMouseLeave={() => setIsMenuHovered(false)}
							bg={showMenuIcon ? 'bg.subtle' : 'transparent'}
							_hover={{
								bg: 'bg.subtle',
							}}
						>
							{/* Menu Icon - shows on hover or when menu is open */}
							{showMenuIcon ? (
								<Box data-component="topic-node-menu-icon" display="flex" alignItems="center" justifyContent="center" borderRadius="full">
									<DotsHorizontalOutline width={6} height={6} size="xs" color="text.softer" borderRadius="full" />
								</Box>
							) : (
								/* Avatar - shows when not hovering */
								<Box width={8} height={8} display="flex" alignItems="center" justifyContent="center">
									{createdBy ? <UserAvatar width="28px" height="28px" userId={createdBy} /> : <TopicIcon width="28px" height="28px" />}
								</Box>
							)}
						</MenuButton>

						<MenuList maxW={rem(280)} data-component="topic-quick-menu">
							<MenuItem onClick={handleToggleRepeat}>
								<HStack gap={2}>
									{repeats ? <StopOutline width="24px" height="24px" color="text.softer" /> : <RefreshOutline width="24px" height="24px" color="text.softer" />}
									<Text>{repeats ? "Don't repeat" : 'Repeat topic'}</Text>
								</HStack>
							</MenuItem>

							{title && (
								<MenuItem onClick={handleOpenDetails}>
									<HStack gap={2}>
										<ZoomInOutline width="24px" height="24px" color="text.softer" />
										<Text>Open topic details</Text>
									</HStack>
								</MenuItem>
							)}

							{title && (
								<MenuItem onClick={handleSearchHistory}>
									<HStack gap={2}>
										<SearchOutline width="24px" height="24px" color="text.softer" />
										<Text>Search history</Text>
									</HStack>
								</MenuItem>
							)}

							<MenuItem onClick={handleDelete}>
								<HStack gap={2}>
									<TrashOutline width="24px" height="24px" color="text.softer" />
									<Text>Remove from 1:1</Text>
								</HStack>
							</MenuItem>
						</MenuList>
					</Menu>

					{/* Repeat indicator badge */}
					{repeats && (
						<Tooltip label="This topic repeats in future meetings" placement="top">
							<Box
								position="absolute"
								bottom="-4px"
								right="-4px"
								bg="bg.subtle"
								border="1px solid"
								borderColor="bg.surface"
								borderRadius="full"
								p="2px"
								width="18px"
								height="18px"
								display="flex"
								justifyContent="center"
								alignItems="center"
								boxShadow="sm"
								cursor="default"
								pointerEvents="none"
							>
								<RepeatHeavyIcon width="14px" height="14px" />
							</Box>
						</Tooltip>
					)}
				</Flex>

				{/* Right side: Topic Content */}
				<Box flex={1} data-node-id={id} position="relative">
					{/* Title input */}
					<Input
						ref={titleInputRef}
						data-component="topic-title-input"
						data-topic-title
						value={title}
						onChange={handleTitleChange}
						onKeyDown={handleTitleKeyDown}
						placeholder="Topic title..."
						fontWeight="semibold"
						fontSize="md"
						color="text.default"
						border="none"
						px={0}
						py={0}
						mt={0}
						w="full"
						maxW="full"
						h={8}
						_focus={{
							boxShadow: 'none',
							borderColor: 'transparent',
						}}
					/>

					{/* Content area */}
					<Box
						ref={contentWrapperRef}
						sx={{
							'& > div': {
								outline: 'none',
							},
							// Topic content text styles
							'& .topic-content p': {
								color: 'text.softer',
								fontSize: 'md',
								fontWeight: 'normal',
								lineHeight: '1.5rem',
								paddingBottom: 3,
								marginBottom: 0,
							},
							'& .topic-content li p': {
								paddingBottom: '0',
								lineHeight: '1.5rem',
							},
							'& .topic-content h1': {
								fontSize: '2xl',
								fontWeight: 'bold',
								marginTop: 4,
								marginBottom: 2,
							},
							'& .topic-content h2': {
								fontSize: 'xl',
								fontWeight: 'semibold',
								marginTop: 3,
								marginBottom: 2,
							},
							'& .topic-content h3': {
								fontSize: 'lg',
								fontWeight: 'semibold',
								marginTop: 2,
								marginBottom: 1,
							},
							'& .topic-content ul, & .topic-content ol': {
								paddingLeft: 6,
								marginBottom: 2,
							},
							'& .topic-content li': {
								marginBottom: 1,
							},
							'& .topic-content blockquote': {
								borderLeft: '4px solid',
								borderColor: 'border.default',
								paddingLeft: 4,
								marginY: 2,
								color: 'text.softer',
								fontStyle: 'italic',
							},
							'& .topic-content code': {
								bg: 'bg.subtle',
								px: 1,
								py: 0.5,
								borderRadius: 'sm',
								fontSize: 'sm',
								fontFamily: 'mono',
							},
							'& .topic-content pre': {
								bg: 'bg.subtle',
								p: 3,
								borderRadius: 'md',
								overflow: 'auto',
								marginY: 2,
							},
							'& .topic-content img': {
								maxW: 'full',
								borderRadius: 'md',
								marginY: 2,
							},
						}}
					>
						<NodeViewContent className="topic-content" />
					</Box>
				</Box>
			</Flex>
		</NodeViewWrapper>
	);
};

// ============================================================================
// TipTap Node Extension
// ============================================================================

export const TopicNode = Node.create<TopicNodeOptions>({
	name: 'topic',

	group: 'block',

	content: '(paragraph | bulletList | orderedList)+',

	defining: true,

	addOptions() {
		return {
			userId: undefined,
		};
	},

	addAttributes() {
		return {
			id: {
				default: null,
				// Use data-node-id for consistency with Repeatable extension
				parseHTML: (element) => element.getAttribute('data-node-id') || element.getAttribute('data-topic-id'),
				renderHTML: (attributes) => ({
					'data-node-id': attributes.id,
				}),
			},
			title: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-title'),
				renderHTML: (attributes) => ({
					'data-title': attributes.title,
				}),
			},
			createdBy: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-created-by'),
				renderHTML: (attributes) => {
					if (!attributes.createdBy) return {};
					return { 'data-created-by': attributes.createdBy };
				},
			},
			// Repeat attributes
			repeats: {
				default: false,
				parseHTML: (element) => element.getAttribute('data-repeats') === 'true',
				renderHTML: (attributes) => {
					if (!attributes.repeats) return {};
					return { 'data-repeats': 'true' };
				},
			},
			repeatTemplate: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-repeat-template'),
				renderHTML: (attributes) => {
					if (!attributes.repeatTemplate) return {};
					return { 'data-repeat-template': attributes.repeatTemplate };
				},
			},
			// Lineage attributes
			copiedFrom: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-copied-from'),
				renderHTML: (attributes) => {
					if (!attributes.copiedFrom) return {};
					return { 'data-copied-from': attributes.copiedFrom };
				},
			},
			copiedByUser: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-copied-by-user'),
				renderHTML: (attributes) => {
					if (!attributes.copiedByUser) return {};
					return { 'data-copied-by-user': attributes.copiedByUser };
				},
			},
			originalNodeId: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-original-node-id'),
				renderHTML: (attributes) => {
					if (!attributes.originalNodeId) return {};
					return { 'data-original-node-id': attributes.originalNodeId };
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: 'div[data-type="topic"]',
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'topic' }), 0];
	},

	addNodeView() {
		return ReactNodeViewRenderer(TopicNodeView);
	},

	addCommands() {
		return {
			insertTopic:
				(attributes?: Partial<TopicAttributes>) =>
				({ commands }: { commands: any }) => {
					const id = attributes?.id || generateNodeId();
					const title = attributes?.title || '';
					const repeats = attributes?.repeats || false;
					const userId = this.options.userId;
					// For new topics marked as repeating, originalNodeId = id (this is the original)
					// For copied topics, originalNodeId should be passed in attributes
					const originalNodeId = repeats ? attributes?.originalNodeId || id : null;

					return commands.insertContent([
						{
							type: this.name,
							attrs: {
								id,
								title,
								// Always set createdBy to the current user when creating a new topic
								// unless it's explicitly provided (e.g., when copying from backend)
								createdBy: attributes?.createdBy || userId || null,
								repeats,
								repeatTemplate: repeats ? generateTopicTemplate(id, title, originalNodeId || undefined) : null,
								copiedFrom: attributes?.copiedFrom || null,
								copiedByUser: repeats ? attributes?.copiedByUser || userId || null : null,
								originalNodeId,
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'paragraph',
							content: [],
						},
					]);
				},

			toggleTopicRepeat:
				(topicId: string) =>
				({ state, dispatch }: { state: any; dispatch: any }) => {
					const { doc, tr } = state;
					let found = false;
					const userId = this.options.userId;

					doc.descendants((node: any, pos: number) => {
						if (node.type.name === 'topic' && node.attrs.id === topicId) {
							const newRepeats = !node.attrs.repeats;
							// When enabling, set originalNodeId to existing or this node's ID
							// When disabling, clear it
							const newOriginalNodeId = newRepeats ? node.attrs.originalNodeId || topicId : null;
							const newTemplate = newRepeats ? generateTopicTemplate(topicId, node.attrs.title, newOriginalNodeId) : null;

							tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								repeats: newRepeats,
								repeatTemplate: newTemplate,
								copiedByUser: newRepeats ? userId : null,
								originalNodeId: newOriginalNodeId,
							});
							found = true;
							return false;
						}
					});

					if (found && dispatch) {
						dispatch(tr);
					}
					return found;
				},

			updateTopicTitle:
				(topicId: string, title: string) =>
				({ state, dispatch }: { state: any; dispatch: any }) => {
					const { doc, tr } = state;
					let found = false;

					doc.descendants((node: any, pos: number) => {
						if (node.type.name === 'topic' && node.attrs.id === topicId) {
							tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								title,
								// Regenerate template if repeating
								...(node.attrs.repeats && {
									repeatTemplate: generateTopicTemplate(topicId, title, node.attrs.originalNodeId),
								}),
							});
							found = true;
							return false;
						}
					});

					if (found && dispatch) {
						dispatch(tr);
					}
					return found;
				},
		} as any;
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey('topicNodeNavigation'),
				props: {
					handleKeyDown: (view, event) => {
						// Only handle ArrowUp to move from content to title
						if (event.key !== 'ArrowUp') return false;
						if (event.shiftKey || event.metaKey || event.ctrlKey) return false;

						const { state } = view;
						const { selection } = state;
						const { $from } = selection;

						// Only handle if no text is selected
						if (!selection.empty) return false;

						// Find parent topic node
						let topicNode = null;
						let topicDepth = -1;

						for (let depth = $from.depth; depth > 0; depth--) {
							const node = $from.node(depth);
							if (node.type.name === 'topic') {
								topicNode = node;
								topicDepth = depth;
								break;
							}
						}

						if (!topicNode) return false;

						// Check if we're in the first child of the topic node
						const childIndex = $from.index(topicDepth);

						// Only intercept if we're in the first child AND at the visual top
						if (childIndex === 0 && view.endOfTextblock('up', state)) {
							// We're at the top line of the first child, focus the title input
							const topicElement = view.dom.querySelector(`[data-node-id="${topicNode.attrs.id}"]`);
							const titleInput = topicElement?.querySelector('[data-topic-title]') as HTMLInputElement;

							if (titleInput) {
								event.preventDefault();
								requestAnimationFrame(() => {
									titleInput.focus();
									// Set cursor to end of input
									const titleLength = titleInput.value.length;
									titleInput.setSelectionRange(titleLength, titleLength);
								});
								return true;
							}
						}

						return false;
					},
				},
			}),
		];
	},
});

// ============================================================================
// Hook for managing topics
// ============================================================================

export const useTopics = (editor: any) => {
	const addTopic = (title?: string, repeats?: boolean) => {
		if (!editor) return;
		(editor.commands as any).insertTopic({ title, repeats });
		setTimeout(() => {
			const titleInputs = document.querySelectorAll('[data-topic-title]');
			const lastTitleInput = titleInputs[titleInputs.length - 1] as HTMLInputElement;
			lastTitleInput?.focus();
		}, 0);
	};

	const getAllTopics = () => {
		if (!editor) return [];
		const topics: Array<{
			id: string;
			title: string;
			repeats: boolean;
			position: number;
		}> = [];

		editor.state.doc.descendants((node: any, pos: number) => {
			if (node.type.name === 'topic') {
				topics.push({
					id: node.attrs.id,
					title: node.attrs.title,
					repeats: node.attrs.repeats || false,
					position: pos,
				});
			}
		});

		return topics;
	};

	const deleteTopic = (topicId: string) => {
		if (!editor) return;
		const { doc, tr } = editor.state;
		let pos: number | null = null;
		let size = 0;

		doc.descendants((node: any, nodePos: number) => {
			if (node.type.name === 'topic' && node.attrs.id === topicId) {
				pos = nodePos;
				size = node.nodeSize;
				return false;
			}
		});

		if (pos !== null) {
			editor.view.dispatch(tr.delete(pos, pos + size));
		}
	};

	const toggleRepeat = (topicId: string) => {
		if (!editor) return;
		(editor.commands as any).toggleTopicRepeat(topicId);
	};

	return {
		addTopic,
		getAllTopics,
		deleteTopic,
		toggleRepeat,
	};
};
